import React, { FC, useMemo } from 'react'
import { Card, ReceiptCard, RefundCardTitle, TextButton } from '~/app/components'
import InformationPanel, {
  InformationFormat,
} from '~/app/components/order-list/details/InformationPanel'
import { CANCELLATION_STATUS } from '~/app/utils/OrderListUtils'
import styled from '~/app/components/styled'
import { OrderDetailsCancellationQuery } from '~/app/components/order-list/hooks/useCancelInfo'
import CancelRefundNotice from '~/app/components/order-cancel/CancelRefundNotice'
import { PaymentType } from '~/__generated__/__types__'

/**
 * RefundInformation component
 * author: soojin.oh
 */

// type definitions
export type RefundCardInfo = {
  representativeGoodsName: string
  itemCount: number
  status: CANCELLATION_STATUS
  date: string
  response: OrderDetailsCancellationQuery[number]
  information: Array<InformationFormat>
}
type Props = {
  cardsInformation: Array<RefundCardInfo>
  onButtonClick: (index: number) => void
  isDisabledViewDetail?: boolean
  createdAt?: string
}

// styled components
const RefundCard = styled(Card)`
  flex-direction: column;
  padding: 0 0 2rem;
  border: solid 0.1rem ${({ theme }) => theme.color.gray200};

  :nth-of-type(1) {
    margin-top: 0;
  }

  :nth-last-of-type(1) {
    margin-bottom: 0;
  }
`

const RefundNoticeWrapper = styled.div`
  margin-bottom: 1.2rem;

  .noticeWrapper {
    padding: 0;
  }
`

// RefundInformation component
const RefundInformation: FC<Props> = function ({
  cardsInformation,
  onButtonClick,
  isDisabledViewDetail,
  createdAt,
}) {
  const cards = useMemo(
    () =>
      cardsInformation.map(
        ({ representativeGoodsName, itemCount, status, date, information }, index) => (
          <RefundCard key={index} hasShadow={!isDisabledViewDetail}>
            <RefundCardTitle
              representativeGoodsName={representativeGoodsName}
              itemCount={itemCount}
              status={status}
              date={date}
              rightAppended={
                !isDisabledViewDetail && (
                  <TextButton
                    style={{ whiteSpace: 'nowrap', marginLeft: '0.8rem', marginTop: '-0.8rem' }}
                    title={'자세히 보기'}
                    type={'basic'}
                    typography={'body2'}
                    onClick={() => onButtonClick(index)} // 주문 취소에는 ID 가 존재하지 않아서 인덱스 정보를 사용하도록 함
                    showArrow={true}
                  />
                )
              }
            />
            <InformationPanel information={information} />
          </RefundCard>
        )
      ),
    [cardsInformation]
  )

  /**
   * 취소시점이 2회 이상인 경우 최신 취소시점 기준 정책 표기
   * 배열의 마지막 번째가 가장 최신 취소 시점임.
   */
  const { paymentMethod, cancelAt } = useMemo(() => {
    const info = cardsInformation[cardsInformation.length - 1].response
    const pg = info.payments.find(payment => payment.type === PaymentType.Pg)
    return {
      paymentMethod: pg?.method,
      cancelAt: info.createdAt,
    }
  }, [cardsInformation])

  return (
    <ReceiptCard title={'취소 및 환불 정보'} isDisabledViewDetail={isDisabledViewDetail}>
      <div style={{ padding: '0 2rem' }}>
        {!isDisabledViewDetail && (
          <RefundNoticeWrapper>
            <CancelRefundNotice
              paymentPgMethod={paymentMethod}
              cancelAt={cancelAt}
              createdAt={createdAt}
            />
          </RefundNoticeWrapper>
        )}
        {cards}
      </div>
    </ReceiptCard>
  )
}

export default RefundInformation
